import {
    Alert,
    Tabs as AntTabs,
    TabsProps as AntTabsProps,
    Avatar,
    Space,
} from 'antd';
import { useEffect } from 'react';

import { ListProfileDto, Roles } from '@clh/api-client';
import { unwrapError } from '@clh/ui';

import { useApiCache } from '../../hooks/use-api-cache';
import { useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';
import { useToast } from '../../hooks/use-toast';
import ProfileNameMenu from '../../shared/profile-name-menu';
import Spinner from '../../spinner/spinner';

import AssessmentsTab from './assessments';
import BgChecksTab from './bg-checks';
import NotesTab from './notes';
import NursingCredentialsTab from './nursing-credentials';
import ProfileTab from './profile';
import VerifiedFacilitiesTab from './verified-facilities';

export default function Tabs() {
    const api = useApiClient();
    const { notification } = useToast();
    const { selection } = useSelection<ListProfileDto>();

    const { loading, record, error } = useApiCache(
        async () => {
            const [
                { roles },
                { permissions },
                { avatar },
                profile,
                nurseProfileCompleteness,
            ] = await Promise.all([
                api!.profileControllerGetUserRoles({
                    profileId: selection.id,
                }),
                api!.profileControllerGetUserPermissions({
                    profileId: selection.id,
                }),
                api!
                    .profileControllerGetAvatar({
                        profileId: selection.id,
                    })
                    .catch(() => ({
                        avatar: null,
                    })),
                api!.profileControllerGetProfile({
                    profileId: selection.id,
                }),
                selection.roles.includes(Roles.Nurse)
                    ? api!.nurseControllerGetProfileCompleteness({
                          profileId: selection.id,
                      })
                    : null,
            ]);

            return {
                ...profile,
                avatar,
                roles,
                nurseProfileCompleteness,
                permissions,
            };
        },
        'profileControllerGetProfile' + selection.id,
        !!api
    );

    useEffect(() => {
        if (error) {
            notification.error({ message: unwrapError(error), duration: 3 });
        }
    }, [error]);

    if (error) {
        return <Alert type="error" message="Profile could not be loaded" />;
    }

    if (loading || !record) {
        return (
            <Space align="center" style={{ width: '100%' }}>
                <Spinner />
            </Space>
        );
    }

    const tabs: AntTabsProps['items'] = [];

    if (record) {
        tabs.push(
            ...[
                {
                    label: 'Profile',
                    key: `tab-profile`,
                    children: <ProfileTab />,
                },
            ]
        );

        if (record.roles.includes(Roles.Nurse)) {
            tabs.push(
                ...[
                    {
                        label: 'Credentials',
                        key: 'tab-nurse-profile',
                        children: <NursingCredentialsTab />,
                    },
                    {
                        label: 'Facilities',
                        key: 'tab-verified-facilities',
                        children: <VerifiedFacilitiesTab />,
                    },
                    {
                        label: 'BG Checks',
                        key: 'tab-bg-checks',
                        children: <BgChecksTab />,
                    },
                    {
                        label: 'Assessments',
                        key: 'assessments',
                        children: <AssessmentsTab />,
                    },
                    {
                        label: 'Notes',
                        key: 'notes',
                        children: <NotesTab />,
                    },
                ]
            );
        }
    }

    return (
        <>
            <Space
                direction="vertical"
                align="center"
                style={{ width: '100%' }}
            >
                <Avatar
                    size="large"
                    src={record.avatar ?? selection.avatar ?? undefined}
                >
                    {record.firstName[0]} {record.lastName[0]}
                </Avatar>

                <ProfileNameMenu
                    profileId={record.id}
                    firstName={record.firstName}
                    lastName={record.lastName}
                    showDocumentAudits={record.roles.includes(Roles.Nurse)}
                />
            </Space>

            <AntTabs items={tabs} />
        </>
    );
}
